import Promise from 'bluebird';
import Task from './Task';
import { isNotNumber } from '../../util/numbers';
import { isNil } from '../../util/object';

/**
 * ConcurrentTaskQueue - a queue of Tasks that can be run concurrently.
 */
export default class ConcurrentTaskQueue {
  _tasks = null;
  _tasksHaveRun = false;
  _taskParallelCount = 1;
  _runningTasks = null;
  _isCancelled = false;

  constructor(taskParallelCount = 1) {
    if (isNotNumber(taskParallelCount) || taskParallelCount < 0) {
      throw new Error('Invalid task batch size');
    }

    this._tasks = new Set();
    this._runningTasks = new Set();

    this._taskParallelCount = taskParallelCount;
  }

  /**
   * addTask - adds a task to the tasks set.
   * It is worth noting that according to the spec, iterating over a set happens in insertion order.
   * @param {Task} task
   */
  addTask(task) {
    if (this._isCancelled) {
      throw new Error('Cannot add tasks after queue is cancelled');
    }

    if (this._tasksHaveRun) {
      throw new Error('Cannot add tasks after queue has run');
    }

    if (!Task.isTask(task)) {
      throw new Error('task must be an instance of class Task');
    }

    if (this._tasks.has(task)) {
      throw new Error('Task already in queue, can only be added once');
    }

    this._tasks.add(task);
  }

  /**
   * run - begins concurently running tasks based on the configured batch size. O
   * nce one task is complete it starts the next pending task.
   * This method can only be run one time.
   * @returns {*} A promise that resolves when all tasks are complete.
   */
  run() {
    if (this._isCancelled) {
      return Promise.reject(
        new Error('SequentialTaskQueue was already cancelled'),
      );
    }

    if (this._tasksHaveRun) {
      return Promise.reject(
        new Error('SequentialTaskQueue can only be run once'),
      );
    }

    this._tasksHaveRun = true;

    if (this._tasks.size === 0) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const taskList = [...this._tasks];

      const runTask = (task) => {
        if (isNil(task)) {
          const error = new Error('Cannot run nil task');
          reject(error);
          return Promise.reject(error);
        }

        this._runningTasks.add(task);
        return task
          .run()
          .then(() => {
            this._runningTasks.delete(task);

            // TODO keep track of values?
            // If task list is depleted, no need to run a new one.
            if (taskList.length === 0) {
              // If running task list is also empty, we are done.
              if (this._runningTasks.size === 0) {
                resolve();
              }
              return Promise.resolve();
            }

            const nextTask = taskList.shift();
            return runTask(nextTask);
          })
          .catch((error) => {
            this.cancel();
            reject(error);
          });
      };

      const parallelTaskCount = Math.min(
        this._taskParallelCount,
        taskList.length,
      );
      for (let i = 0; i < parallelTaskCount; i++) {
        const task = taskList.shift();
        runTask(task);
      }
    });
  }

  /**
   * cancel - attempts to stops the task queue.
   */
  cancel() {
    this._isCancelled = true;
    if (this._tasksHaveRun) {
      this._tasks.forEach((task) => task.cancel());
    }
    this._tasks.clear();
    this._runningTasks.clear();
  }
}
